import { Link, Navigate, Route, Routes, useLocation, useNavigationType, useParams } from "react-router-dom";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "./lib/api";
import { AuthProvider, useAuth } from "./lib/AuthContext";
import { ConfigProvider } from "./lib/ConfigContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Library from "./pages/Library";
import { Brand } from "./components/Brand";
import { StaleChunkBoundary } from "./components/StaleChunkBoundary";
import { AudiobookPlayerProvider } from "./lib/AudiobookPlayer";

// Lazy-loaded — each gets its own chunk so the initial payload stays lean.
// Reader drags in react-pdf + pdfjs-dist (~400 KB). Wizard has a large
// component tree. Atelier + BookDetail are also route-scoped.
// Studio is the user-facing creation surface. As of the old-Wizard revert
// (feat/revert-ui) it mounts the 13-step Wizard (pages/Wizard.tsx) again, backed by
// the new engine + Bible. /new and /wizard continue to 301 to /studio.
const Studio      = lazy(() => import("./pages/Wizard"));
// v2.81 — Atelier (the conversational chat surface) consolidated into the
// Studio. All /atelier* routes now redirect to /studio. Code archived at
// _archive/atelier_v1/. AdminAtelier (read-only ops view) is unaffected.
const BookDetail  = lazy(() => import("./pages/BookDetail"));
const BookLanding = lazy(() => import("./pages/BookLanding"));
const BookWorkspace = lazy(() => import("./pages/BookWorkspace"));
// Full-bleed book editor (editor-poc layout) — its own route, outside the
// status-gated workspace. Authors land here once a book is `ready`/`published`.
const EditorRoute = lazy(() => import("./editor/EditorRoute"));
const BuildById   = lazy(() => import("./pages/BuildById"));
const Reader        = lazy(() => import("./pages/Reader"));
const ChapterReader = lazy(() => import("./pages/ChapterReader"));
// FE-26 — the author's critique report + review desk. Replaces the dead ChapterReview page
// (deleted: its three endpoints had zero hits in backend/app), whose /books/:id/review path
// now redirects here. Dev's ChapterReview import is dropped WITH the page — the file is gone
// from this tree, and the /books/:id/review route below is the redirect that replaced it.
const Critique   = lazy(() => import("./pages/Critique"));
// RF-10 — where "Submit and revise" lands: the change map, the run, and the revision report.
// Lazy for the usual reason plus one specific to it: the whole `src/revise/` lane (surface,
// store, report, version history) is dead weight in the initial payload for every author who
// has not yet frozen a brief.
const Revise     = lazy(() => import("./pages/Revise"));
// FE-22 / FE-23 (wave D) — the Senior Editor's own surface, and the invited reader's front
// door. Both are lazy for the usual reason and one extra: neither is reachable for the vast
// majority of accounts, so neither belongs in the initial payload.
const EditorDesk = lazy(() => import("./pages/EditorDesk"));
const EditorBook = lazy(() => import("./pages/EditorDesk").then((m) => ({ default: m.EditorBook })));
const SharedDraft = lazy(() => import("./pages/SharedDraft"));
const AudiobookPlayerPage = lazy(() => import("./pages/AudiobookPlayerPage"));
const DreamBook   = lazy(() => import("./pages/DreamBook"));
const DBEngine    = lazy(() => import("./pages/DBEngine"));
const About       = lazy(() => import("./pages/About"));
const Workshop    = lazy(() => import("./pages/Workshop"));
const Marketing   = lazy(() => import("./pages/Marketing"));
const Marketplace = lazy(() => import("./pages/Marketplace"));
const MyBooks     = lazy(() => import("./pages/MyBooks"));
const BookStatus  = lazy(() => import("./pages/BookStatus"));
// The publishing wizard — "creation wizard, phase 2". Author-only, and reached from the
// ready_to_publish CTA on Book Status / My Books (see lifecycle/statusCta.ts).
const PublishWizard = lazy(() => import("./pages/publish/PublishWizard"));
const Pricing     = lazy(() => import("./pages/Pricing"));
const Contact     = lazy(() => import("./pages/Contact"));
const Privacy     = lazy(() => import("./pages/Privacy"));
const Terms       = lazy(() => import("./pages/Terms"));
const Refunds     = lazy(() => import("./pages/Refunds"));
const Account     = lazy(() => import("./pages/Account"));
const AuthorPage  = lazy(() => import("./pages/AuthorPage"));
const DeconstructedReader = lazy(() => import("./pages/DeconstructedReader"));
const NavMockups = lazy(() => import("./pages/NavMockups"));
// Throwaway — Studio right-panel design exploration. URL-only, not in nav.
const StudioMocks = lazy(() => import("./pages/StudioMocks"));
// Throwaway — hardcoded redesign proposal for the live book-build surface.
const BookBuildMockup = lazy(() => import("./pages/BookBuildMockup"));
// Throwaway — long-reader stress test for background chapter availability updates.
const ChapterReaderLiveMockup = lazy(() => import("./pages/ChapterReaderLiveMockup"));
// Throwaway — hardcoded audiobook flow for product review. No API wiring.
const AudiobookMockup = lazy(() => import("./pages/AudiobookMockup"));
// Internal QA harness for title/vision/cover-image generation. URL-only, not in nav.
const TestImageGen = lazy(() => import("./pages/TestImageGen"));
// AI-critique report mockups (F7-01..08). Dummy data only, URL-only, not in nav. The v1
// lane (FE-03, `/critique-mocks`) retired at F7-16 cleanup once v2 wired: its own fixtures
// (`critique/mockData.ts`) and the `ReportShell` composition they fed both went with it.
const CritiqueV2Mocks = lazy(() => import("./pages/CritiqueV2Mocks"));
// The same Review surface at its LIVE height contract — one viewport, panes scroll, footer
// pinned. Dummy data only, URL-only, not in nav; the catalogue lane above cannot show it
// because it lives in a scrolling document.
const CritiqueViewportMocks = lazy(() => import("./pages/CritiqueViewportMocks"));
// What Big Picture shows WHILE a round reads (the 2026-08-12 "looks dead but is working"
// incident). Dummy data only, URL-only, not in nav.
const CritiqueRunningMocks = lazy(() => import("./pages/CritiqueRunningMocks"));
// AI-revise degraded-state harness (RF-03). Q4 dissolved the mockup gate and kept exactly
// this: degraded states only, rendered through the REAL components. URL-only, not in nav.
const ReviseMocks = lazy(() => import("./pages/ReviseMocks"));
// Parallel-research wizard step (dummy data only). URL-only, not in nav.
const ResearchParallelMocks = lazy(() => import("./pages/ResearchParallelMocks"));
// MOCKUP — the My Books lifecycle redesign. Dummy data only, URL-only, not in nav.
const MyBooksMocks = lazy(() => import("./pages/lifecycle/MyBooksMocks"));
// Vision free-write locked-summary mockup (dummy data, URL-only, not in nav).
const VisionFreewriteMocks = lazy(() => import("./pages/VisionFreewriteMocks"));
// MOCKUP — five-stage book status mission control. Dummy data only, URL-only, not in nav.
const BookStatusMocks = lazy(() => import("./pages/lifecycle/BookStatusMocks"));
// The publishing wizard against a fixture — the REAL component tree, unauthenticated, so the
// author-only surface can be reviewed and walked in a browser without a session.
const PublishWizardMocks = lazy(() => import("./pages/publish/PublishWizardMocks"));
// MOCKUP — the book-manager queue: "with your book manager" + the questions gate. Dummy data
// only, URL-only, not in nav.
const BmQueueMocks = lazy(() => import("./pages/lifecycle/BmQueueMocks"));
// MOCKUP — library search field + Browse-all pagination. Dummy data only, URL-only, not in nav.
const LibrarySearchMocks = lazy(() => import("./pages/library/searchMocks"));

// Admin panel — allowlist-gated control surface at /admin/* (rebuilt). Three lean
// surfaces: the user roster, the invite-only request queue, and access settings.
const AdminLayout   = lazy(() => import("./admin/AdminLayout").then((m) => ({ default: m.AdminLayout })));
const AdminUsers    = lazy(() => import("./admin/pages/Users").then((m) => ({ default: m.Users })));
const AdminBooks    = lazy(() => import("./admin/pages/Books").then((m) => ({ default: m.Books })));
const AdminBookPdf  = lazy(() => import("./admin/pages/BookPdfView").then((m) => ({ default: m.BookPdfView })));
const AdminRequests = lazy(() => import("./admin/pages/Requests").then((m) => ({ default: m.Requests })));
const AdminSettings = lazy(() => import("./admin/pages/Settings").then((m) => ({ default: m.Settings })));
const AdminExportBook = lazy(() => import("./admin/pages/ExportBookHub").then((m) => ({ default: m.ExportBookHub })));
// MOCKUP — admin print-production studio research, dummy data only and no API imports.
const ExportBookMock = lazy(() => import("./admin/pages/ExportBookMock").then((m) => ({ default: m.ExportBookMock })));
const AdminControlPlaneV2 = lazy(() => import("./admin/v2/AdminControlPlaneV2"));


function Loading() {
  return (
    <div className="min-h-screen grid place-items-center">
      <div className="text-center">
        <Brand />
        <p className="mt-4 text-xs text-ink-300 smallcaps">Opening the observatory…</p>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  return <>{children}</>;
}

/**
 * Ownership gate for the author-only surfaces (editor / workspace / review).
 *
 * `ProtectedRoute` only proves you're signed in, so any logged-in user who was sent an
 * `/editor` link could open the full editing shell over someone else's book. Nothing
 * could actually be written — every mutating endpoint goes through `books_svc.get_owned`
 * and 403s a non-owner — but an editor that looks live over a book you didn't write is
 * misleading, and on a published book it silently invites edits that can never save.
 *
 * The predicate mirrors the backend's `is_owner` exactly (strict owner), so the
 * invariant is simple: these surfaces open as WRITABLE iff the backend would accept
 * your writes.
 *
 * One deliberate exception, opt-in per route via `adminReadOnly`: admins. The backend
 * grants admins read access to any book (`require_access` / `can_read`, BR-005.5 —
 * oversight is read-only; every write still goes through strict-owner `get_owned`).
 * The editor route opts in so "Editor ↗" from /admin/books opens the real editor in
 * its locked Reader mode instead of bouncing to the not-the-author screen. The
 * write-shaped surfaces (workspace / review) do not opt in.
 */
function AuthorOnlyRoute({ children, adminReadOnly = false }: { children: ReactNode; adminReadOnly?: boolean }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  const { id } = useParams<{ id: string }>();
  const [verdict, setVerdict] = useState<"checking" | "author" | "admin" | "denied">("checking");
  const [book, setBook] = useState<{ title: string; status: string } | null>(null);

  useEffect(() => {
    if (!ready || !user || !id) return;
    let cancel = false;
    setVerdict("checking");
    (async () => {
      try {
        const b = await api.get<{ owner_id: string; title: string; status: string }>(`/api/books/${id}`);
        if (cancel) return;
        setBook({ title: b.title, status: b.status });
        if (b.owner_id === user.id) setVerdict("author");
        // Role comes from the session (/auth/me projection) — the same signal the
        // admin panel gates on. The fetch above succeeding proves the backend grants
        // this admin read access to the book, whatever its status.
        else if (adminReadOnly && user.role === "admin") setVerdict("admin");
        else setVerdict("denied");
      } catch {
        // 404 here is either "no such book" or "someone else's unpublished book":
        // `get_readable` refuses to distinguish the two so it can't be used to probe
        // for existence, so neither do we.
        if (!cancel) { setBook(null); setVerdict("denied"); }
      }
    })();
    return () => { cancel = true; };
  }, [ready, user, id, adminReadOnly]);

  if (!ready) return <Loading />;
  if (!user) return <Navigate to="/login" replace state={{ from: loc.pathname + loc.search }} />;
  if (verdict === "checking") return <Loading />;
  if (verdict === "denied") return <NotTheAuthor bookId={id} book={book} />;
  if (verdict === "admin") {
    // Oversight is read-only: the editor locks itself to Reader mode on ?read=1.
    // Force the flag on so even a hand-typed /editor URL can't paint the writing
    // shell over someone else's book (writes would 404 server-side regardless).
    const params = new URLSearchParams(loc.search);
    if (params.get("read") !== "1") {
      params.set("read", "1");
      return <Navigate to={`${loc.pathname}?${params.toString()}`} replace />;
    }
  }
  return <>{children}</>;
}

/** Shown when someone opens an author-only link for a book they didn't write. */
function NotTheAuthor({ bookId, book }: { bookId?: string; book: { title: string; status: string } | null }) {
  // We could read it — for a regular user that means it's published-public, so offer the
  // reader. If we couldn't, it's unpublished or nonexistent; say so without confirming
  // which. `published` is checked against the actual status rather than inferred from
  // readability: an admin can read a book of ANY status, so "readable" alone must never
  // be spoken of as "it's published".
  const readable = book !== null;
  const published = readable && book.status === "published";
  return (
    <div className="min-h-screen grid place-items-center px-6">
      <div className="text-center max-w-[440px]">
        <Brand />
        <p className="mt-6 font-serif italic text-ink-100 text-lg leading-snug">
          {readable ? "This book belongs to another author." : "This book isn't available yet."}
        </p>
        <p className="mt-3 text-[13px] text-ink-300 font-ui leading-relaxed">
          {readable ? (
            <>
              {book?.title ? <span className="italic">“{book.title}”</span> : "This book"} isn't yours to
              edit, so the editor is closed
              {published
                ? " — but it's published, so you can read the whole thing."
                : " — and it hasn't been published yet."}
            </>
          ) : (
            "It hasn't been published yet, and it looks like you're not its author. Check back once the author publishes it."
          )}
        </p>
        <div className="mt-7 flex items-center justify-center gap-3">
          {published && bookId ? (
            <Link to={`/books/${bookId}`} className="btn-primary">Read this book</Link>
          ) : null}
          <Link to="/library" className="btn-subtle">Browse the library</Link>
        </div>
      </div>
    </div>
  );
}

function GuestOnlyRoute({ children }: { children: ReactNode }) {
  const { user, ready } = useAuth();
  const loc = useLocation();
  if (!ready) return <Loading />;
  // Once signed in, return to the page that prompted the login (carried in
  // `state.from` by ProtectedRoute) rather than always dumping the user on the
  // homepage. This also wins the race with Login/GoogleButton's own redirect:
  // signIn() sets the user before their nav() runs, so this route re-renders
  // first — it must send the user to the same place they were headed.
  if (user) {
    const from = (loc.state as { from?: string } | null)?.from ?? "/library";
    return <Navigate to={from} replace />;
  }
  return <>{children}</>;
}

// The legacy live-build page (/building) is retired by the Engine pivot — the
// status-gated workspace IS the build surface now. Keep the URL working.
function WorkspaceRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/books/${id}/workspace`} replace />;
}

// FE-26 — `/books/:id/review` reclaimed. `ChapterReview.tsx` was deleted with the route: its
// backend endpoints (`/chapter-reviews`, `/chapters/{n}/review`) have zero hits in
// `backend/app`, so the page could only ever have 404'd. Old links (BookDetail still has one)
// land on the critique instead of on a dead surface.
function ReviewRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/books/${id}/critique`} replace />;
}

// D9 (critique-v2 doc 00) spells the Review surface `/book/:id/critique` — singular. The app's
// canonical namespace is plural (`/books/:id/*` throughout this file), so the locked path is
// honored as an alias rather than as a second mount: one page, one guard, one place where the
// surface is composed. `replace` so Back does not bounce between the two spellings. The query
// string carries the tab and selection (doc 07 §1.1), so it is preserved verbatim.
function CritiqueAliasRedirect() {
  const { id } = useParams<{ id: string }>();
  const { search, hash } = useLocation();
  return <Navigate to={`/books/${id}/critique${search}${hash}`} replace />;
}

// CloudFront's SPA fallback rewrites every extensionless path to index.html,
// so static files in /public are only reachable with their extension. This
// bounces the clean URL to the real file (react-router can't Navigate to a
// non-SPA document).
function StaticFileRedirect({ href }: { href: string }) {
  useEffect(() => { window.location.replace(href); }, [href]);
  return null;
}

// Reset the window scroll to the top on every real route change. Without this
// a client-side navigation keeps the previous page's scroll offset, so the new
// page opens partway down. Keyed on pathname only (search/hash-only changes —
// e.g. My Books' ?tab= switch — keep their position), and skips POP so the
// browser can restore the prior offset on back/forward.
function ScrollToTop() {
  const { pathname } = useLocation();
  const navType = useNavigationType();
  const prevPath = useRef(pathname);
  useEffect(() => {
    if (pathname === prevPath.current) return; // search/hash change on same page
    prevPath.current = pathname;
    if (navType === "POP") return;             // back/forward → let the browser restore
    window.scrollTo(0, 0);
  }, [pathname, navType]);
  return null;
}

export default function App() {
  return (
    <ConfigProvider>
      <AuthProvider>
        <AudiobookPlayerProvider>
          <ScrollToTop />
          <StaleChunkBoundary>
          <Suspense fallback={<Loading />}>
            <Routes>
          <Route path="/login" element={<GuestOnlyRoute><Login /></GuestOnlyRoute>} />
          <Route path="/register" element={<GuestOnlyRoute><Register /></GuestOnlyRoute>} />
          {/* Public surfaces — published books are readable by anyone. The
              page components themselves show CTAs to sign in or register
              when a logged-out visitor tries to use author-only features. */}
          <Route path="/library" element={<Library />} />
          <Route path="/marketplace" element={<Marketplace />} />
          <Route path="/author/:userId" element={<AuthorPage />} />
          <Route path="/books/:id" element={<BookLanding />} />
          <Route path="/books/:id/legacy" element={<BookDetail />} />
          <Route path="/books/:id/building" element={<ProtectedRoute><WorkspaceRedirect /></ProtectedRoute>} />
          {/* Pivot surface: status-gated Engine build → Editor read/edit (BookWorkspace). */}
          <Route path="/books/:id/workspace" element={<AuthorOnlyRoute><BookWorkspace /></AuthorOnlyRoute>} />
          <Route path="/books/:id/status" element={<AuthorOnlyRoute><BookStatus /></AuthorOnlyRoute>} />
          <Route path="/books/:id/publish" element={<AuthorOnlyRoute><PublishWizard /></AuthorOnlyRoute>} />
          {/* Full-bleed editor (editor-poc layout). The workspace redirects here once ready.
              AuthorOnly, not merely Protected: a shared link must not open someone else's
              book in an editing shell (writes were always refused, but the UI implied
              otherwise). Non-authors get the reader instead — except admins, whose
              oversight opens the editor locked to Reader mode (adminReadOnly + ?read=1). */}
          <Route path="/books/:id/editor" element={<AuthorOnlyRoute adminReadOnly><EditorRoute /></AuthorOnlyRoute>} />
          <Route path="/builds/:build_id" element={<ProtectedRoute><BuildById /></ProtectedRoute>} />
          <Route path="/books/:id/critique" element={<AuthorOnlyRoute><Critique /></AuthorOnlyRoute>} />
          {/* RF-10 — the revise surface. `AuthorOnlyRoute` for the critique route's reason and
              more forcefully: this page SPENDS — it is where the cost handshake is answered and
              a run is started. The server owns the real gate (`books_svc.get_owned` on every
              endpoint in `app/api/revise.py`), and this keeps a shared link from opening a
              surface whose primary control bills someone else's account.
              `?brief=` and `?run=` are read by the page; both are optional. */}
          <Route path="/books/:id/revise" element={<AuthorOnlyRoute><Revise /></AuthorOnlyRoute>} />
          {/* FE-26 — the SPA's version of a 301: the old chapter-review path is reclaimed by
              the critique surface, `replace` so Back does not bounce between the two. */}
          <Route path="/books/:id/review" element={<ReviewRedirect />} />
          {/* D9's spelling of the Review surface. See `CritiqueAliasRedirect`. */}
          <Route path="/book/:id/critique" element={<CritiqueAliasRedirect />} />
          {/* FE-22 — the Senior Editor. `ProtectedRoute` only proves you are signed in;
              authorisation is the OPEN ASSIGNMENT on the book, checked server-side by
              `editors_svc.get_assigned`, which 404s rather than 403s. There is deliberately
              no client-side role gate: a role check here would be a second, weaker copy of
              the rule, and the surface it would guard renders nothing without the server's
              data anyway. */}
          <Route path="/editorial" element={<ProtectedRoute><EditorDesk /></ProtectedRoute>} />
          <Route path="/editorial/books/:id" element={<ProtectedRoute><EditorBook /></ProtectedRoute>} />
          {/* FE-23 — the invited reader's front door. The path and the `?invite=` key are
              built by `email.share_accept_link()`; they are a server fact, not a choice.
              NOT ProtectedRoute: an unauthenticated visitor must see what they are being
              invited to and a sign-in link that carries them back here WITH the token,
              rather than being bounced to /login and losing the invitation. */}
          <Route path="/shared/:id" element={<SharedDraft />} />
          <Route path="/books/:id/read" element={<Reader />} />
          <Route path="/books/:id/listen" element={<AudiobookPlayerPage />} />
          <Route path="/books/:id/chapter/:n" element={<ChapterReader />} />
          <Route path="/books/:id/blocks/:n" element={<DeconstructedReader />} />
          {/* Creation chooser — visible to everyone; the Studio it links to
              gates behind auth. Keeps /dreambook shareable with beta
              users who haven't signed up yet. */}
          <Route path="/dreambook" element={<DreamBook />} />
          {/* Writers Workshop — public: logged-out visitors get the per-tool
              explainers + sign-up CTA; the live builders render only for
              authenticated users (gated inside Workshop.tsx, not by route). */}
          <Route path="/workshop" element={<Workshop />} />
          <Route path="/db-engine" element={<DBEngine />} />
          <Route path="/about" element={<About />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refunds" element={<Refunds />} />
          {/* Author-only surfaces */}
          <Route path="/my-books" element={<ProtectedRoute><MyBooks /></ProtectedRoute>} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          {/* Internal — visual decision surfaces. No auth needed; mockup-only.
              Lives at /nav-mockups (not /admin/*) so it bypasses the
              gate without needing a separate nginx allowlist entry. */}
          <Route path="/nav-mockups" element={<NavMockups />} />
          {/* Weekly founder report — static HTML in /public. */}
          <Route path="/week29" element={<StaticFileRedirect href="/week29.html" />} />
          <Route path="/studio-mocks" element={<StudioMocks />} />
          <Route path="/critique-v2-mocks" element={<CritiqueV2Mocks />} />
          <Route path="/critique-viewport-mocks" element={<CritiqueViewportMocks />} />
          <Route path="/critique-running-mocks" element={<CritiqueRunningMocks />} />
          <Route path="/revise-mocks" element={<ReviseMocks />} />
          <Route path="/research-parallel-mocks" element={<ResearchParallelMocks />} />
          <Route path="/admin-export-mockup/:id?" element={<ExportBookMock />} />
          <Route path="/my-books-mocks" element={<MyBooksMocks />} />
          <Route path="/vision-freewrite-mocks" element={<VisionFreewriteMocks />} />
          <Route path="/book-status-mocks" element={<BookStatusMocks />} />
          <Route path="/publish-wizard-mocks" element={<PublishWizardMocks />} />
          <Route path="/bm-queue-mocks" element={<BmQueueMocks />} />
          <Route path="/library-search-mocks" element={<LibrarySearchMocks />} />
          <Route path="/book-build-mockup" element={<BookBuildMockup />} />
          <Route path="/book-build-reader-mockup" element={<ChapterReaderLiveMockup />} />
          <Route path="/audiobook-mockup" element={<AudiobookMockup />} />
          <Route path="/test-image-gen" element={<TestImageGen />} />
          <Route path="/admin-v2" element={<AdminControlPlaneV2 />} />
          {/* Admin panel — allowlist-gated control surface at /admin/*.
              AdminLayout guards on the bearer session (role === "admin");
              a signed-out visitor goes to /login, a non-admin goes home. */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/users" replace />} />
            <Route path="users"    element={<AdminUsers />} />
            <Route path="books"    element={<AdminBooks />} />
            <Route path="books/:id/pdf" element={<AdminBookPdf />} />
            <Route path="books/:id/export" element={<AdminExportBook />} />
            <Route path="requests" element={<AdminRequests />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>
          {/* Legacy admin paths → the new roster home. */}
          <Route path="/admin-react" element={<Navigate to="/admin/users" replace />} />
          <Route path="/admin-react/*" element={<Navigate to="/admin/users" replace />} />
          {/* /studio is the canonical creation surface (renamed from /new
              and /wizard 2026-05-27). One surface, both fresh-start and
              upload-first manuscript paths flow through here. */}
          <Route path="/studio" element={<ProtectedRoute><Studio /></ProtectedRoute>} />
          {/* Legacy redirects — bookmarks survive the rename. */}
          <Route path="/new" element={<Navigate to="/studio" replace />} />
          <Route path="/wizard" element={<Navigate to="/studio" replace />} />
          {/* Atelier consolidated into Studio 2026-05-27 — code archived at
              _archive/atelier_v1/; route paths kept as redirects so any
              live tabs / bookmarks survive. */}
          <Route path="/atelier" element={<Navigate to="/studio" replace />} />
          <Route path="/atelier/:id" element={<Navigate to="/studio" replace />} />
          <Route path="/atelier-old/:id" element={<Navigate to="/studio" replace />} />
          <Route path="/atelier-v2/:id" element={<Navigate to="/studio" replace />} />
          {/* Marketing landing — same content for logged-in and logged-out,
              so the page is shareable with beta testers without surprises. */}
          <Route path="/" element={<Marketing />} />
          <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
          </StaleChunkBoundary>
        </AudiobookPlayerProvider>
      </AuthProvider>
    </ConfigProvider>
  );
}
